import { nanoid } from "nanoid";
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { Props, ToastContainer, ToastContext } from "./Toast.Parts";
import { ToastMessage } from "./toast-message/ToastMessage";
import { ToastMessageProps } from "./toast-message/ToastMessage.Parts";

const ToastContextX = createContext<ToastContext>({
  addToast: () => "",
  removeToast: () => null,
  toastContainerRef: { current: null },
});

export const ToastProvider = ({ children }: Props) => {
  const [toasts, setToasts] = useState<ToastMessageProps[]>([]);
  const toastContainerRef = useRef<HTMLUListElement>(null);

  const removeToast: ToastContext["removeToast"] = useCallback(
    (id) => {
      setToasts((toasts) => toasts.filter((toast) => toast.id !== id));
    },
    [setToasts],
  );

  const addToast: ToastContext["addToast"] = useCallback(
    (props) => {
      const id = nanoid(16);
      setToasts((toasts) => [{ id, removeToast, ...props }, ...toasts]);
      return id;
    },
    [setToasts, removeToast],
  );

  return (
    <ToastContextX.Provider
      value={{ addToast, removeToast, toastContainerRef }}
    >
      <ToastContainer ref={toastContainerRef}>
        {toasts.map((toast) => (
          <ToastMessage key={toast.id} {...toast} />
        ))}
      </ToastContainer>
      {children}
    </ToastContextX.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContextX);
  if (context === undefined)
    throw new Error("useToast must be used within a ToastProvider");

  return context;
};
